import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type ComposeData = Schema<'ComposeResponse'>
type Step = Schema<'FollowUpStepOut'>
type Preview = Schema<'MessagePreviewResponse'>

/** `BRIEF_LENGTHS` and `BRIEF_CTAS`, in the words `outreach.html#compose` uses. */
const LENGTHS: readonly { value: string; label: string }[] = [
  { value: 'short', label: 'Short — under 90 words' },
  { value: 'medium', label: 'Medium — 90 to 150 words' },
]

const CTAS: readonly { value: string; label: string }[] = [
  { value: 'call', label: 'A 15-minute call' },
  { value: 'timeline', label: 'A reply with their timeline' },
  { value: 'profile', label: 'Permission to send a profile' },
]

/**
 * Mirrors `BriefRequest` and `CapRequest` in `api/app/v1/compose.py` (`MAX_OFFER`,
 * `MAX_ICP`, `follow_ups_per_day: Field(ge=1, le=500)`). Bounded here as well as
 * on the server because the 422 the server sends says only "Request validation
 * failed" with no field named, so a tenant who pastes a 2,100-character offer is
 * told to guess which of six controls is wrong (WEB-14, WEB-15).
 */
const OFFER_MAX = 2000
const ICP_MAX = 500
const CAP_MIN = 1
const CAP_MAX = 500

/** Channels a rung may use. Mirrors `CHANNEL_CONTACT_KINDS` — a channel with no
 *  address kind cannot reach a lead, and the API refuses one. */
const CHANNELS: readonly { value: string; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
  { value: 'rcs', label: 'RCS' },
  { value: 'linkedin', label: 'LinkedIn' },
]

/**
 * "What we say" — `design/screens/outreach.html#compose`, audit row §B4.
 *
 * Three things behind one page: the brief every message is generated from, the
 * ladder of rungs it goes out on, and the button that starts it.
 *
 * WHY THERE IS NO TEMPLATE BOX, WHICH IS THE WHOLE SCREEN
 * --------------------------------------------------------
 * The prototype states it: *"§5 asks for a message tailored to each prospect, so
 * there is no template box on this screen — what you write is the brief, and
 * every message is generated against the post that lead was found in. The
 * preview is a real prospect, not a placeholder with `{{first_name}}` in it,
 * because a merge-field preview hides exactly the failure you are checking for."*
 *
 * So *Preview* generates a real message for a real lead, using the same two model
 * calls a send makes, and spends the same money doing it. It is a button rather
 * than something the screen does on load for that reason.
 *
 * WHY SAVING AND STARTING ARE TWO PRESSES
 * -----------------------------------------
 * `PUT /outreach/brief` writes the brief and its `active` switch;
 * `POST /outreach/follow-ups/start` queues a first touch for everybody who has
 * never been written to. The prototype draws one button, and one button here
 * would be one press for two decisions — *"this is what we say"* and *"send it to
 * four hundred people"* — with no way back between them. The screen keeps the
 * prototype's single primary action and asks for confirmation in between, naming
 * the number.
 *
 * WHY A MEMBER SEES THIS READ-ONLY RATHER THAN NOT AT ALL
 * --------------------------------------------------------
 * `GET` is `inbox:read`, which every role holds, and every write is
 * `outreach:manage`, which stops at admin. A member who cannot see what their
 * organisation is sending cannot use the inbox they are being asked to answer.
 */
export function Compose(): JSX.Element {
  const { role } = useActiveTenant()
  const manageable = may(role, 'outreach:manage')
  const toast = useToast()

  const compose = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/brief', { signal })),
    [],
  )

  if (compose.state.status === 'loading') return <Loading label="Loading what we say" lines={8} />
  if (compose.state.status === 'error')
    return (
      <div className="ar-stack ar-stack--loose">
        <h1 className="ar-h1">What we say</h1>
        <ErrorState failure={compose.state.failure} onRetry={compose.refetch} />
      </div>
    )

  return (
    <Editor
      data={compose.state.data}
      manageable={manageable}
      onSaved={() => {
        compose.refetch()
      }}
      toast={toast}
    />
  )
}

function Editor({
  data,
  manageable,
  onSaved,
  toast,
}: {
  data: ComposeData
  manageable: boolean
  onSaved: () => void
  toast: ReturnType<typeof useToast>
}): JSX.Element {
  const [offer, setOffer] = useState(data.brief.offer)
  const [icp, setIcp] = useState(data.brief.icp)
  const [length, setLength] = useState(data.brief.length)
  const [cta, setCta] = useState(data.brief.cta)
  const [quotePost, setQuotePost] = useState(data.brief.quote_post)
  const [steps, setSteps] = useState<Step[]>(data.steps)
  // WEB-15. The raw string, not a number: `Number('')` is 0, so clearing the
  // field put a value the backend refuses (minimum 1) into state, `min`/`max`
  // never saw it — they constrain the spinner, not what may be typed — and Save
  // sent it. Keeping the text means "empty" stays distinguishable from "zero".
  const [cap, setCap] = useState(String(data.daily_cap))
  const [confirming, setConfirming] = useState(false)
  const [preview, setPreview] = useState<Preview | null>(null)

  // The server is the source of truth after every save, and a re-read has to
  // reach the form — otherwise a save that the server normalised (a trimmed
  // offer, a seeded ladder) leaves the screen showing what was typed rather than
  // what was stored.
  useEffect(() => {
    setOffer(data.brief.offer)
    setIcp(data.brief.icp)
    setLength(data.brief.length)
    setCta(data.brief.cta)
    setQuotePost(data.brief.quote_post)
    setSteps(data.steps)
    setCap(String(data.daily_cap))
  }, [data])

  const saveBrief = useMutation((active: boolean) =>
    call(() =>
      api.PUT('/api/v1/outreach/brief', {
        body: { offer, icp, length, cta, quote_post: quotePost, active },
      }),
    ),
  )
  const saveLadder = useMutation((rows: Step[]) =>
    call(() =>
      api.PUT('/api/v1/outreach/follow-ups/ladder', {
        body: {
          steps: rows.map((row: Step) => ({
            step_no: row.step_no,
            label: row.label,
            wait_days: row.wait_days,
            channel: row.channel,
            angle: row.angle,
            enabled: row.enabled,
          })),
        },
      }),
    ),
  )
  const saveCap = useMutation((value: number) =>
    call(() => api.PUT('/api/v1/outreach/follow-ups/cap', { body: { follow_ups_per_day: value } })),
  )
  const generate = useMutation(() =>
    call(() => api.POST('/api/v1/outreach/brief/preview', { body: { step_no: 1 } })),
  )
  const start = useMutation(() => call(() => api.POST('/api/v1/outreach/follow-ups/start', {})))

  const busy = saveBrief.pending || saveLadder.pending || saveCap.pending || start.pending

  // The same shape as `lengthError` on `ContentPipeline`: the number the request
  // would carry, or `undefined` and a sentence saying why there isn't one.
  const capNumber = Number(cap)
  const capValid =
    cap.trim() !== '' && Number.isInteger(capNumber) && capNumber >= CAP_MIN && capNumber <= CAP_MAX
  const capValue = capValid ? capNumber : undefined
  const capError = capValid
    ? undefined
    : `A whole number between ${String(CAP_MIN)} and ${String(CAP_MAX)}.`
  // The cap is displayed in four places that want a number. While the field is
  // being edited into an invalid state, what is actually in force is still the
  // stored value — so that is what they are told, rather than a `0` or a `NaN`.
  const capInForce = capValue ?? data.daily_cap

  const onSave = (active: boolean): void => {
    void (async () => {
      // Unreachable through the buttons, which are disabled on `capError`, but
      // `onSave` is also reached from the confirm dialog and this is the last
      // point before a request the backend would refuse with an envelope that
      // names no field.
      if (capValue === undefined) return
      const brief = await saveBrief.run(active)
      if (!brief.ok) return
      const ladder = await saveLadder.run(steps)
      if (!ladder.ok) return
      if (capValue !== data.daily_cap) {
        const capped = await saveCap.run(capValue)
        if (!capped.ok) return
      }
      toast.show({
        kind: 'success',
        title: 'Saved',
        body: active
          ? 'Sending is on. Nothing goes out until you start the first touches.'
          : 'Sending is off, so nothing will go out.',
      })
      onSaved()
    })()
  }

  const onStart = (): void => {
    void (async () => {
      const brief = await saveBrief.run(true)
      if (!brief.ok) return
      // WEB-7. The ladder save's result was discarded and `start` ran anyway,
      // so *Save & start sending* began the campaign on the **previously
      // saved** ladder whenever the new one was refused — and the refusal
      // reached the screen only after the first touches had been queued.
      // `onSave` above has always checked this; this path did not.
      const ladder = await saveLadder.run(steps)
      if (!ladder.ok) {
        setConfirming(false)
        return
      }
      const started = await start.run(undefined)
      setConfirming(false)
      if (started.ok) {
        toast.show({
          kind: 'success',
          title:
            started.data.queued === 0
              ? 'Everybody reachable is already in the ladder'
              : `Queued ${String(started.data.queued)} first touches`,
          body: `${String(started.data.pending)} leads are now mid-ladder. They go out inside your send window, up to ${String(capInForce)} a day.`,
        })
        onSaved()
      }
    })()
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <h1 className="ar-h1">What we say</h1>
        <span className="app-spacer" />
        {manageable ? (
          <div className="ar-btn-group">
            <Button
              size="sm"
              busy={generate.pending}
              onClick={() => {
                void (async () => {
                  const result = await generate.run(undefined)
                  if (result.ok) setPreview(result.data)
                })()
              }}
            >
              Preview a real message
            </Button>
            <Button
              size="sm"
              tone="secondary"
              busy={busy}
              disabled={capError !== undefined}
              onClick={() => {
                onSave(data.brief.active)
              }}
            >
              Save
            </Button>
            <Button
              size="sm"
              tone="primary"
              disabled={offer.trim() === '' || capError !== undefined}
              onClick={() => {
                setConfirming(true)
              }}
            >
              Save &amp; start sending
            </Button>
          </div>
        ) : null}
      </div>

      <InlineError
        failure={saveBrief.state.failure ?? saveLadder.state.failure ?? saveCap.state.failure}
      />
      <InlineError failure={start.state.failure} />
      <InlineError failure={generate.state.failure} />

      <SendingState data={data} cap={capInForce} />

      <div className="app-lead">
        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <h3>Your brief</h3>
              <span className="ar-card__spacer" />
              <span className="ar-subtle app-lead__meta">Used for every message</span>
            </div>
            <div className="ar-card__body ar-stack">
              <Field
                label="What you're offering"
                help="Goes into every message as the only things AugRank is allowed to claim you do. One capability per line."
              >
                {(props) => (
                  <textarea
                    {...props}
                    className="ar-textarea"
                    rows={5}
                    maxLength={OFFER_MAX}
                    disabled={!manageable}
                    value={offer}
                    onChange={(event) => {
                      setOffer(event.target.value)
                    }}
                  />
                )}
              </Field>
              <Field
                label="Who it's for"
                help="This sentence also scores each lead — the score on a lead detail is measured against it."
              >
                {(props) => (
                  <input
                    {...props}
                    className="ar-input"
                    type="text"
                    maxLength={ICP_MAX}
                    disabled={!manageable}
                    value={icp}
                    onChange={(event) => {
                      setIcp(event.target.value)
                    }}
                  />
                )}
              </Field>
              <div className="app-compose__pair">
                <Field label="Length">
                  {(props) => (
                    <select
                      {...props}
                      className="ar-select"
                      disabled={!manageable}
                      value={length}
                      onChange={(event) => {
                        setLength(event.target.value)
                      }}
                    >
                      {LENGTHS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Ask for">
                  {(props) => (
                    <select
                      {...props}
                      className="ar-select"
                      disabled={!manageable}
                      value={cta}
                      onChange={(event) => {
                        setCta(event.target.value)
                      }}
                    >
                      {CTAS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
              <label className="ar-switch">
                <input
                  type="checkbox"
                  disabled={!manageable}
                  checked={quotePost}
                  onChange={(event) => {
                    setQuotePost(event.target.checked)
                  }}
                />
                <span className="ar-switch__track" aria-hidden="true" />
                <span>
                  Quote the post back to them
                  <span className="ar-choice__desc">
                    Reply rates roughly double. It is also the only proof this was not a blast.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <Ladder steps={steps} manageable={manageable} onChange={setSteps} />

          <div className="ar-card">
            <div className="ar-card__header">
              <h3>How much goes out</h3>
            </div>
            <div className="ar-card__body ar-stack">
              <Field
                label="Follow-ups a day"
                help="Counted across every channel and every step, in your own day (IST). The send window in Settings decides the hours; this decides the volume."
                error={capError}
              >
                {(props) => (
                  <input
                    {...props}
                    className="ar-input"
                    type="number"
                    min={CAP_MIN}
                    max={CAP_MAX}
                    disabled={!manageable}
                    value={cap}
                    onChange={(event) => {
                      setCap(event.target.value)
                    }}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>

        <div className="ar-stack">
          <PreviewCard preview={preview} />
          <div className="ar-alert ar-alert--info">
            <span className="ar-alert__icon" aria-hidden="true">
              ◷
            </span>
            <div className="ar-alert__body">
              <strong>Every message carries an unsubscribe</strong>
              One click, no login, and the address is suppressed before the next send can start. It
              is added by the sender itself, so it cannot be left out by accident.
            </div>
          </div>
        </div>
      </div>

      {confirming ? (
        <ConfirmStart
          ready={data.ready}
          pending={data.pending}
          cap={capInForce}
          busy={busy}
          onCancel={() => {
            setConfirming(false)
          }}
          onConfirm={onStart}
        />
      ) : null}
    </div>
  )
}

/** What is happening right now, above the form. The one thing a tenant opening
 *  this screen actually wants to know. */
function SendingState({ data, cap }: { data: ComposeData; cap: number }): JSX.Element {
  if (!data.brief.active)
    return (
      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>Sending is off</strong>
          Nothing goes out to anybody while it is. Write the brief, check a preview against a real
          prospect, and press <em>Save &amp; start sending</em> when it reads the way you would
          write it yourself.
        </div>
      </div>
    )
  return (
    <div className="ar-alert ar-alert--success">
      <span className="ar-alert__icon" aria-hidden="true">
        ✓
      </span>
      <div className="ar-alert__body">
        <strong>Sending is on</strong>
        {data.sent_today} of {cap} follow-ups have gone out today, and {data.pending} leads are
        mid-ladder. Any reply, on any channel, stops the ladder for that person.
      </div>
    </div>
  )
}

/**
 * The ladder table.
 *
 * **`wait_days` is labelled *after the previous step*, not as the prototype's
 * absolute offsets**, because that is what it means: a rung that could not go out
 * on its day — a shut send window, a spent cap — moves the ones after it rather
 * than letting them bunch up against it. `app/outreach/followups.py` argues it at
 * length; the column heading is where a tenant meets the decision.
 */
function Ladder({
  steps,
  manageable,
  onChange,
}: {
  steps: Step[]
  manageable: boolean
  onChange: (steps: Step[]) => void
}): JSX.Element {
  const update = (index: number, patch: Partial<Step>): void => {
    onChange(steps.map((step, at) => (at === index ? { ...step, ...patch } : step)))
  }

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Follow-up ladder</h3>
        <span className="ar-card__spacer" />
        {manageable ? (
          <Button
            size="sm"
            disabled={steps.length >= 10}
            onClick={() => {
              const next = (steps[steps.length - 1]?.step_no ?? 0) + 1
              onChange([
                ...steps,
                {
                  step_no: next,
                  label: `Step ${String(next)}`,
                  wait_days: 3,
                  channel: 'email',
                  angle: '',
                  enabled: true,
                },
              ])
            }}
          >
            Add a step
          </Button>
        ) : null}
      </div>
      <div className="ar-table-wrap">
        <table className="ar-table">
          <thead>
            <tr>
              <th>Step</th>
              <th>Wait after the previous step</th>
              <th>Channel</th>
              <th>Angle</th>
              <th>On</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step, index) => (
              <tr key={step.step_no}>
                <td>
                  <strong>{step.step_no}</strong>{' '}
                  <input
                    className="ar-input app-compose__label"
                    type="text"
                    aria-label={`Name for step ${String(step.step_no)}`}
                    disabled={!manageable}
                    value={step.label}
                    onChange={(event) => {
                      update(index, { label: event.target.value })
                    }}
                  />
                </td>
                <td>
                  {step.step_no === 1 ? (
                    <span className="ar-muted">Immediately</span>
                  ) : (
                    <input
                      className="ar-input app-compose__days"
                      type="number"
                      min={0}
                      max={90}
                      aria-label={`Days to wait before step ${String(step.step_no)}`}
                      disabled={!manageable}
                      value={step.wait_days}
                      onChange={(event) => {
                        update(index, { wait_days: Number(event.target.value) })
                      }}
                    />
                  )}
                </td>
                <td>
                  <select
                    className="ar-select app-compose__channel"
                    aria-label={`Channel for step ${String(step.step_no)}`}
                    disabled={!manageable}
                    value={step.channel}
                    onChange={(event) => {
                      update(index, { channel: event.target.value })
                    }}
                  >
                    {CHANNELS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="ar-input"
                    type="text"
                    aria-label={`Angle for step ${String(step.step_no)}`}
                    disabled={!manageable}
                    value={step.angle}
                    onChange={(event) => {
                      update(index, { angle: event.target.value })
                    }}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Step ${String(step.step_no)} is on`}
                    disabled={!manageable}
                    checked={step.enabled}
                    onChange={(event) => {
                      update(index, { enabled: event.target.checked })
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="ar-card__footer">
        <span className="ar-subtle app-lead__meta">
          Any reply, on any channel, ends the ladder for that person. So does an unsubscribe, a
          STOP, or a bounce — those also add them to the suppression list.
        </span>
      </div>
    </div>
  )
}

/** The preview card. Empty until somebody presses the button, because generating
 *  one spends money and a screen that spent money on load would be one. */
function PreviewCard({ preview }: { preview: Preview | null }): JSX.Element {
  if (preview === null)
    return (
      <div className="ar-card">
        <div className="ar-card__body">
          <Empty
            icon="○"
            title="No preview yet"
            body="Press Preview a real message and AugRank writes one for your highest-scoring uncontacted lead, from their own post. It costs a model call, which is why it is a button rather than something this page does on its own."
          />
        </div>
      </div>
    )

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Preview — {preview.lead_name ?? 'a real prospect'}</h3>
        <span className="ar-card__spacer" />
        <span className="ar-badge ar-badge--neutral">{preview.channel}</span>
      </div>
      <div className="ar-card__body ar-stack ar-stack--tight">
        {preview.post_text === null ? null : (
          <div className="ar-panel">
            <span className="ar-subtle app-lead__meta">Their post</span>
            <p className="app-lead__post">{preview.post_text}</p>
          </div>
        )}
        {preview.subject === null ? null : (
          <div>
            <span className="ar-subtle app-lead__meta">Subject</span>
            <p className="app-compose__preview">{preview.subject}</p>
          </div>
        )}
        <p className="app-compose__preview">{preview.body}</p>
      </div>
      <div className="ar-card__footer">
        <span className="ar-subtle app-lead__meta">
          {preview.tailored
            ? 'Written by Haiku 4.5 against their post.'
            : 'The model could not be reached, so this is the standard wording. A real send would read the same way until it can be.'}
        </span>
      </div>
    </div>
  )
}

/** The step between *"this is what we say"* and *"send it to four hundred
 *  people"*, which the prototype's single button does not have. */
function ConfirmStart({
  ready,
  pending,
  cap,
  busy,
  onCancel,
  onConfirm,
}: {
  ready: number
  pending: number
  cap: number
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}): JSX.Element {
  return (
    <div className="ar-card app-compose__confirm" role="group" aria-label="Start sending">
      <div className="ar-card__body ar-stack ar-stack--tight">
        <strong>
          {ready === 0
            ? 'Nothing new to queue'
            : `Queue a first touch for ${String(ready)} ${ready === 1 ? 'lead' : 'leads'}?`}
        </strong>
        <span className="ar-subtle app-lead__meta">
          {ready === 0
            ? `Every reachable lead is already in the ladder — ${String(pending)} of them. Starting again queues nothing.`
            : `They go out inside your send window, at most ${String(cap)} a day, and each one is written against that prospect's own post. ${String(pending)} leads are already mid-ladder.`}
        </span>
      </div>
      <div className="ar-card__footer">
        <Button onClick={onCancel}>Not yet</Button>
        <span className="ar-card__spacer" />
        <Button tone="primary" busy={busy} onClick={onConfirm}>
          Start sending
        </Button>
      </div>
    </div>
  )
}
